/**
 * Export recording, used only in export mode (/play/:id?export=en|ur), which
 * the server opens in a hidden Chrome on this computer (export.service.ts).
 * The page records its own tab (picture and sound) while the film plays and
 * hands the video to the server piece by piece through functions the server
 * put on `window`. Nothing leaves the computer.
 */

/** Container and codec to record, best first (Chrome records MP4). */
const TYPES = ['video/mp4;codecs=avc1.42E01E,mp4a.40.2', 'video/mp4;codecs=avc1,mp4a.40.2', 'video/mp4', 'video/webm;codecs=vp9,opus', 'video/webm'];

/** What the server's hidden Chrome provides (see export.service.ts). */
export interface ExportHost {
  __exportStarted: (mimeType: string) => Promise<void>;
  __exportChunk: (base64: string) => Promise<void>;
  __exportProgress: (positionMs: number, totalMs: number) => Promise<void>;
  __exportDone: () => Promise<void>;
  __exportFailed: (message: string) => Promise<void>;
}

export function exportHost(): ExportHost | null {
  const w = window as unknown as Partial<ExportHost>;
  return typeof w.__exportChunk === 'function' ? (w as ExportHost) : null;
}

/** Largest piece handed to the server in one call. */
const SLICE_BYTES = 2 * 1024 * 1024;

function toBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',')[1] ?? '');
    r.onerror = () => reject(r.error);
    r.readAsDataURL(blob);
  });
}

/**
 * Start recording this tab at the given size. Every second's video goes to
 * the host in order; `stop()` resolves once the last piece has been handed
 * over and the host was told it is complete.
 */
export async function recordForHost(host: ExportHost, size: { width: number; height: number }): Promise<{ stop: () => Promise<void> }> {
  const stream = await navigator.mediaDevices.getDisplayMedia({
    video: { frameRate: 30, width: size.width, height: size.height },
    audio: { suppressLocalAudioPlayback: false },
    preferCurrentTab: true,
    selfBrowserSurface: 'include',
  } as DisplayMediaStreamOptions);
  if (stream.getAudioTracks().length === 0) {
    stream.getTracks().forEach((t) => t.stop());
    throw new Error('The film’s sound could not be recorded.');
  }
  const mimeType = TYPES.find((t) => MediaRecorder.isTypeSupported(t))!;
  const recorder = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 6_000_000, audioBitsPerSecond: 192_000 });
  await host.__exportStarted(mimeType);
  // Pieces are sent strictly in order, in slices: Chrome's MP4 recorder keeps
  // most of the film until it stops, then hands over one large piece.
  let queue = Promise.resolve();
  recorder.ondataavailable = (e) => {
    if (e.data.size === 0) return;
    const piece = e.data;
    queue = queue.then(async () => {
      for (let at = 0; at < piece.size; at += SLICE_BYTES) await host.__exportChunk(await toBase64(piece.slice(at, at + SLICE_BYTES)));
    });
  };
  const stopped = new Promise<void>((resolve) => {
    recorder.onstop = () => {
      stream.getTracks().forEach((t) => t.stop());
      resolve();
    };
  });
  recorder.start(1000);
  return {
    stop: async () => {
      if (recorder.state !== 'inactive') recorder.stop();
      await stopped;
      await queue;
      await host.__exportDone();
    },
  };
}
